import type { InventoryListFilterValue, InventoryListFilterView } from "@slfinrtl/ui-bff-routes"
import { getInventoryListFilterViewRoute } from "@slfinrtl/ui-bff-routes"
import { dequal } from "dequal"
import { useCallback, useEffect, useState } from "react"
import type { UseFormReturn } from "react-hook-form"
import useSWRImmutable from "swr/immutable"
import { removeUndefinedKeys } from "src/common/removeUndefinedKeys"
import { useBffDynamic } from "src/hooks"
import { extractValues } from "./extractValues"

interface InventoryListFilterValueProps {
	bodyTypes?: string[]
	derivatives?: string[]
	modelGenerations?: string[]
	modelSeries?: string
}

type FetcherKey = [InventoryListFilterValueProps]

const parseParams = ({
	bodyTypes,
	derivatives,
	modelGenerations,
	modelSeries,
}: InventoryListFilterValueProps): InventoryListFilterValueProps => ({
	// since relevant filtervalues are only a subset InventoryListFilterValue,
	// the relevant props are set explicity to avoid adding all props to the query
	bodyTypes,
	derivatives,
	modelGenerations,
	modelSeries: !modelSeries ? undefined : modelSeries,
})

export const useInventoryListFilterView = (filterForm: UseFormReturn<InventoryListFilterValue>) => {
	const { reset, watch } = filterForm
	const filterValue = watch()

	const [params, setParams] = useState<InventoryListFilterValueProps>(() => parseParams(filterValue))
	const fetchFilter = useBffDynamic(getInventoryListFilterViewRoute, "inline")

	useEffect(() => {
		function updateParams() {
			const newParams: InventoryListFilterValueProps = parseParams(filterValue)
			if (!dequal(newParams, params)) {
				setParams(newParams)
			}
		}

		void updateParams()
	}, [filterValue, params])

	const getKey = useCallback((): FetcherKey => [params], [params])
	const fetcher = useCallback(
		([queryParams]: FetcherKey) =>
			fetchFilter({
				body: undefined,
				pathParams: {},
				queryParams,
			}),
		[fetchFilter],
	)

	const revalidateFilterValues = useCallback(
		(data: InventoryListFilterView) => {
			const possibleValues = extractValues(data)

			const revalidatedValues: InventoryListFilterValue = {
				...filterValue,
				bodyTypes: filterValue.bodyTypes?.filter((value) => possibleValues.bodyTypes?.includes(value)),
				derivatives: filterValue.derivatives?.filter((value) => possibleValues.derivatives?.includes(value)),
				equipmentTags: filterValue.equipmentTags?.filter((value) =>
					possibleValues.equipmentTags?.includes(value),
				),
				exteriorColors: filterValue.exteriorColors?.filter((value) =>
					possibleValues.exteriorColors?.includes(value),
				),
				interiorColors: filterValue.interiorColors?.filter((value) =>
					possibleValues.interiorColors?.includes(value),
				),
				modelGenerations: filterValue.modelGenerations?.filter((value) =>
					possibleValues.modelGenerations?.includes(value),
				),
				stockStatus: filterValue.stockStatus?.filter((value) => possibleValues.stockStatus?.includes(value)),
			}

			if (!dequal(removeUndefinedKeys(revalidatedValues), removeUndefinedKeys(filterValue))) {
				reset(revalidatedValues, { keepFieldsRef: true })
			}
		},
		[filterValue, reset],
	)

	const response = useSWRImmutable<InventoryListFilterView>(getKey, fetcher, {
		keepPreviousData: true,
		onSuccess: (data) => revalidateFilterValues(data),
	})

	return response
}
